import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ImageSize } from 'shared/utils/image/image-canvas';
import { describeFetchStoredImageFailure, fetchStoredImage } from 'shared/utils/image/upscale/fetch-stored-image';
import { measureImage } from 'shared/utils/image/upscale/measure-image';
import { isSmallImage } from 'shared/utils/image/upscale/upscale-plan';
import { DropzoneUpscaleView, ImageUpscaleOption, SmallImageHintProps } from 'ud-form/components/image-upscale/types';
import { DropzonePreview, replaceItem } from '../domain/previews';
import { DropzoneValue } from '../types';

/** Что мерить: только что выбранный файл — сам файл, сохранённую картинку — по адресу. */
type MeasureSource = File | string;

/** Картинка в модалке улучшения: какой элемент поля она заменит и с чем модель работает. */
interface PendingUpscale {
  item: DropzoneValue;
  file: File;
  size: ImageSize;
}

interface UseDropzoneUpscaleParams {
  upscale?: ImageUpscaleOption;
  multiple: boolean;
  items: DropzoneValue[];
  previews: DropzonePreview[];
  /** Довести файл до формы прежним путём (сжатие) — улучшенный файл идёт тем же конвейером. */
  processFiles: (files: File[]) => Promise<File[]>;
  /** Записать новый список картинок в значение поля. */
  writeItems: (items: DropzoneValue[]) => void;
}

/**
 * Подсказка «картинка мелкая» и ИИ-улучшение — для каждой картинки поля.
 *
 * Мерится всё, что лежит в поле: и только что выбранный файл, и сохранённая картинка (по
 * адресу, CORS для этого не нужен). В одиночном поле — первая картинка, в `multiple` — каждая.
 * Модель не трогается до клика «Улучшить»; сохранённую картинку перед модалкой скачивают —
 * пиксели чужой картинки без CORS не прочитать.
 *
 * Улучшенный файл встаёт на место исходника и дальше идёт прежним путём (сжатие, запись в
 * форму) — сохраняет его обычный сабмит формы.
 */
export function useDropzoneUpscale({ upscale, multiple, items, previews, processFiles, writeItems }: UseDropzoneUpscaleParams) {
  const upscaleKind = upscale?.kind;

  /**
   * Файлы, которые уже прошли улучшение. Фото 120 px после ×4 всё ещё меньше порога
   * (480 < 640), но второй прогон модель уже не восстанавливает, а дорисовывает —
   * подсказку у результата не показываем.
   */
  const upscaledFilesRef = useRef(new WeakSet<File>());

  /**
   * Что мерить у каждой картинки (по индексу). Мерится значение поля, то есть уже после кропа
   * и сжатия: сжатие не трогает картинки меньше 4 МБ и не опускает сторону ниже 1600.
   */
  const sources = useMemo<(MeasureSource | null)[]>(() => {
    if (!upscaleKind) return [];

    const targets = multiple ? items : items.slice(0, 1);
    return targets.map((item, index) => {
      if (item instanceof File) return upscaledFilesRef.current.has(item) ? null : item;
      return previews[index]?.url ?? null;
    });
  }, [upscaleKind, multiple, items, previews]);

  /**
   * Замеры по источнику. Ключ — сам файл или адрес, поэтому устаревший ответ (картинку уже
   * убрали) ни к чему не привяжется, а переставленная картинка не меряется заново.
   */
  const [sizes, setSizes] = useState<Map<MeasureSource, ImageSize>>(() => new Map());
  const startedRef = useRef(new Set<MeasureSource>());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    sources.forEach((source) => {
      if (!source || startedRef.current.has(source)) return;
      startedRef.current.add(source);

      measureImage(source)
        .then((size) => {
          if (isMountedRef.current) setSizes((previous) => new Map(previous).set(source, size));
        })
        // Не смогли измерить — просто не подсказываем: картинку в форме это не портит.
        .catch(() => undefined);
    });
  }, [sources]);

  /** Размер мелкой картинки по индексу, `null` — крупная, не измерена или подсказка не нужна. */
  const smallSizes = useMemo(
    () =>
      sources.map((source) => {
        const size = source ? sizes.get(source) : undefined;
        return size && upscaleKind && isSmallImage(size, upscaleKind) ? size : null;
      }),
    [sources, sizes, upscaleKind],
  );

  /** Картинка, которую сейчас улучшают: ею управляется модалка. */
  const [pendingUpscale, setPendingUpscale] = useState<PendingUpscale | null>(null);

  /** Сохранённая картинка, которую сейчас скачивают перед модалкой. */
  const [downloadingItem, setDownloadingItem] = useState<DropzoneValue | null>(null);
  const downloadRef = useRef<AbortController | null>(null);

  // Ушли с формы посреди скачивания — запрос обрываем, модалку уже некому показывать.
  useEffect(() => () => downloadRef.current?.abort(), []);

  const openUpscale = useCallback(
    async (index: number) => {
      const item = items[index];
      const size = smallSizes[index];
      if (!item || !size) return;

      if (item instanceof File) {
        setPendingUpscale({ item, file: item, size });
        return;
      }

      const url = previews[index]?.url;
      if (!url) return;

      const controller = new AbortController();
      downloadRef.current = controller;
      setDownloadingItem(item);

      try {
        const file = await fetchStoredImage(url, controller.signal);
        if (!controller.signal.aborted) setPendingUpscale({ item, file, size });
      } catch (error) {
        // Значение поля не трогаем: картинка по-прежнему сохранена, улучшить её можно позже.
        if (!controller.signal.aborted) toast.error(describeFetchStoredImageFailure(error));
      } finally {
        if (!controller.signal.aborted) setDownloadingItem(null);
        if (downloadRef.current === controller) downloadRef.current = null;
      }
    },
    [items, smallSizes, previews],
  );

  /** «Применить» — улучшенный файл встаёт на место исходника; `null` — значение не трогаем. */
  const closeUpscale = useCallback(
    async (result: File | null) => {
      const pending = pendingUpscale;
      setPendingUpscale(null);
      if (!result || !pending) return;

      upscaledFilesRef.current.add(result);
      const [processed] = await processFiles([result]);
      if (!processed) return;
      upscaledFilesRef.current.add(processed);

      // Ищем по самому элементу, а не по индексу: пока модалка была открыта, порядок мог смениться.
      const index = items.indexOf(pending.item);
      if (index !== -1) writeItems(replaceItem(items, index, processed));
    },
    [pendingUpscale, processFiles, items, writeItems],
  );

  const view = useMemo<DropzoneUpscaleView | undefined>(() => {
    if (!upscale) return undefined;

    const hintAt = (index: number): SmallImageHintProps | null => {
      const size = smallSizes[index];
      if (!size) return null;

      return {
        kind: upscale.kind,
        size,
        locked: upscale.locked,
        onLockedClick: upscale.onLockedClick,
        onUpscale: () => void openUpscale(index),
        isLoading: downloadingItem !== null && items[index] === downloadingItem,
        compact: multiple,
      };
    };

    return {
      hint: multiple ? null : hintAt(0),
      previewHints: multiple ? smallSizes.map((_, index) => hintAt(index)) : [],
      modal: pendingUpscale
        ? { file: pendingUpscale.file, kind: upscale.kind, size: pendingUpscale.size, onClose: closeUpscale }
        : null,
    };
  }, [upscale, multiple, smallSizes, items, downloadingItem, pendingUpscale, openUpscale, closeUpscale]);

  return {
    view,
    /** Идёт скачивание или открыта модалка — зона закрыта для нового файла. */
    isBusy: pendingUpscale !== null || downloadingItem !== null,
  };
}
